import type { Key } from 'react';
import { cloneDeep } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { isEmptyObject } from '../../common-functions';
import type { JsonData, JsonDataItem } from '../../generic-types';
import type { DynamicFormButtonSetting, DynamicFormField } from '../dynamic-form.interfaces';
import { FieldSetting } from './field-setting';
import type { VisibilityConditionBase } from './visibility-condition-base';
import { VisibilityConditionFactory } from './visibility-condition-factory';
import type { VisibilityConditionConfig, VisibilityConditionGroup } from './visibility-condition.interfaces';

export class VisibilityConditionProcessor {
  // The list of field visibility conditions
  private readonly fieldConditions: Array<VisibilityConditionConfig>;

  // The list of button visibility conditions
  private buttonConditions: Array<VisibilityConditionConfig>;

  /**
   * This is a map of 'sourceFieldId_condition type' -> 'condition id_condition type'
   *
   * For example '4173_OPTIONGROUP' -> '4850_TEXTFIELD',
   * which is used to find out the condition to check when the source field with id 4173 changes
   */
  private reverseFieldMap = new Map<string, Array<Array<string>>>();

  /**
   * This is a map of 'sourceFieldId' -> 'condition types'
   *
   * For example, '4173' -> ["BUTTON", "OPTIONGROUP"],
   * which indicates that this field is involved in those two types of visibility conditions
   */
  private fieldTypeMap = new Map<string, Array<string>>();

  /**
   * This is a map of 'condition id_condition type' -> 'a field setting object'
   *
   * The field setting object contains:
   *  - list of conditions
   *  - the settings of the field (button) for which visibility conditions are configured
   *  - the forms field settings (NEW -> used to check if a field is hidden and the check can be skipped)
   */
  private readonly settingConditionMap = new Map<string, FieldSetting>();

  /**
   * This is a map of 'condition id_condition type' -> 'a field setting object'.
   *
   * It is similar to the `settingConditionMap` but only contains the 'GROUP' condition types
   *
   * The field setting object contains:
   *  - list of conditions
   *  - the settings of the field for which visibility conditions are configured
   */
  private readonly groupOnlyConditionMap = new Map<string, FieldSetting>();

  /**
   * A flat map of field seetings.
   *
   * This map contains all field settings
   */
  private fieldSettingsFlatMap = new Map<Key, DynamicFormField>();

  // The form data
  private data: JsonDataItem;

  // Field and button subjects to subscribe for changes
  private fieldVisibilityChangedSubject = new BehaviorSubject<Array<DynamicFormField> | undefined>(undefined);
  private buttonVisibilityChangedSubject = new BehaviorSubject<Array<DynamicFormButtonSetting> | undefined>(undefined);

  // Flags that indicate a change in visibility has occurred and form should be changed
  private haveFieldSettingsChanged = false;
  private haveButtonSettingsChanged = false;
  private fieldSettings?: Array<DynamicFormField>;
  private buttonSettings?: Array<DynamicFormButtonSetting>;
  private userGroups?: JsonData;

  constructor(
    fieldConditions: Array<VisibilityConditionConfig>,
    buttonConditions: Array<VisibilityConditionConfig>,
    data: JsonDataItem,
    fieldSettings?: Array<DynamicFormField>,
    buttonSettings?: Array<DynamicFormButtonSetting>,
    userGroups?: JsonData
  ) {
    this.fieldSettings = fieldSettings;
    this.buttonSettings = buttonSettings;
    this.userGroups = userGroups;
    this.fieldConditions = cloneDeep(fieldConditions);
    this.buttonConditions = cloneDeep(buttonConditions);
    this.data = cloneDeep(data);

    if (this.fieldSettings && this.fieldSettings.length > 0) {
      this.buildFieldSettingsMap(this.fieldSettings, this.fieldConditions);
      this.buildFieldSettingsFlatMap(this.fieldSettings);
    }

    if (this.buttonSettings && this.buttonSettings.length > 0) {
      this.buildButtonSettingsMap(this.buttonSettings, this.buttonConditions);
    }

    this.buildFieldTypeMapAndReverseFieldMap([...this.fieldConditions, ...this.buttonConditions]);
    this.runAllConditions();

    if (this.haveFieldSettingsChanged) {
      this.fieldVisibilityChangedSubject.next(this.fieldSettings);
    }

    if (this.hasData() && this.haveButtonSettingsChanged) {
      this.buttonVisibilityChangedSubject.next(this.buttonSettings);
    }
  }

  /**
   * When the processor is created with an empty data object this method can be called once the data is loaded.
   * @param {JsonDataItem} data - The data used to run against the visibility conditions.
   */
  public loadData(data: JsonDataItem, currentFormButtons?: Array<DynamicFormButtonSetting>) {
    this.haveFieldSettingsChanged = false;
    this.haveButtonSettingsChanged = false;
    this.data = cloneDeep(data);
    if (!isEmptyObject(this.data)) {
      this.runAllConditions();
    }

    if (this.haveFieldSettingsChanged) {
      this.fieldVisibilityChangedSubject.next(this.fieldSettings);
    }

    if (this.haveButtonSettingsChanged || !currentFormButtons) {
      this.buttonVisibilityChangedSubject.next(this.buttonSettings);
    }
  }

  public updateFields(fieldSettings: Array<DynamicFormField>) {
    if (fieldSettings && fieldSettings.length > 0) {
      this.haveFieldSettingsChanged = false;
      this.fieldSettings = fieldSettings;

      this.updateFieldSettingsFlatMap(this.fieldSettings);

      this.buildFieldSettingsMap(fieldSettings, this.fieldConditions);
      if (!isEmptyObject(this.data)) {
        this.runAllConditions();
      }

      if (this.haveFieldSettingsChanged) {
        this.fieldVisibilityChangedSubject.next(this.fieldSettings);
      }
    }
  }

  public updateButtons(
    buttonConditions: Array<VisibilityConditionConfig>,
    buttonSettings: Array<DynamicFormButtonSetting>,
    currentFormButtons?: Array<DynamicFormButtonSetting>
  ) {
    if (buttonSettings && buttonSettings.length > 0) {
      this.haveButtonSettingsChanged = false;

      this.settingConditionMap.clear();
      this.groupOnlyConditionMap.clear();

      this.buttonSettings = buttonSettings;
      this.buttonConditions = buttonConditions;
      this.buildButtonSettingsMap(this.buttonSettings, this.buttonConditions);

      if (this.fieldSettings && this.fieldSettings.length > 0) {
        this.buildFieldSettingsMap(this.fieldSettings, this.fieldConditions);
      }

      this.fieldTypeMap.clear();
      this.reverseFieldMap.clear();
      this.buildFieldTypeMapAndReverseFieldMap([...this.fieldConditions, ...this.buttonConditions]);

      if (!isEmptyObject(this.data)) {
        this.runAllConditions();
      }
    }

    if (this.hasData() && (this.haveButtonSettingsChanged || !currentFormButtons)) {
      this.buttonVisibilityChangedSubject.next(this.buttonSettings);
    }
  }

  /**
   * Called when a field changes so all affected visibility conditions are run.
   * @param {string} fieldName - The name of the field where the data change occurred.
   * @param fieldValue - The new field value.
   */
  public processChangedField(fieldName: string, fieldValue: unknown) {
    this.haveFieldSettingsChanged = false;
    this.haveButtonSettingsChanged = false;
    this.data[fieldName] = fieldValue;

    if (this.fieldTypeMap.has(fieldName)) {
      const fieldTypes = this.fieldTypeMap.get(fieldName) || [];
      // For each condition type that is attached to this field, run the visibility conditions
      for (let i = 0, length = fieldTypes.length; i < length; i++) {
        // The `${fieldName}_${fieldTypes[i]}` represents `sourceFieldId_condition type` and is a key in the reverseFieldMap
        this.runConditionTree(`${fieldName}_${fieldTypes[i]}`);
      }
    }

    if (this.haveFieldSettingsChanged) {
      this.fieldVisibilityChangedSubject.next(this.fieldSettings);
    }

    if (this.haveButtonSettingsChanged) {
      this.buttonVisibilityChangedSubject.next(this.buttonSettings);
    }
  }

  /**
   * Fires when a fields visibility setting is changed by the processor; it will send the current state of the field
   * settings.
   * @returns {Observable<Array<DynamicFormField> | undefined>} An Observable that the field will be sent through.
   */
  public onFieldVisibilityChanged() {
    return this.fieldVisibilityChangedSubject.asObservable();
  }

  public onButtonVisibilityChanged() {
    return this.buttonVisibilityChangedSubject.asObservable();
  }

  /**
   * Determines if a field only has group visibility conditions since those will run when the processor first starts.
   * @param {Array<VisibilityConditionBase>} conditions - The condition array to check for only group conditions.
   * @returns {boolean} Returns true if the passed in conditions are all group conditions.
   * @private
   */
  private isOnlyGroupConditions(conditions: Array<Array<VisibilityConditionBase>>) {
    return conditions.length > 0
      ? conditions.every(
          (group) => group.length > 0 && group.every((condition) => condition.VisibilityType === 'GROUP')
        )
      : conditions;
  }

  /**
   * Runs all loaded visibility conditions against the current data. This is used when the processor is instantiated or
   * when new data is loaded.
   * @private
   */
  private runAllConditions() {
    this.groupOnlyConditionMap.forEach((setting) => {
      setting.setFieldVisibility(this.data, this.userGroups);
      if (setting.hasSettingChanged) {
        if (!setting.isButtonSetting) {
          this.haveFieldSettingsChanged = true;
        } else {
          this.haveButtonSettingsChanged = true;
        }
      }
    });
    this.reverseFieldMap.forEach((_, key) => {
      this.runConditionTree(key);
    });
  }

  /**
   * Recursively runs the field visibility conditions based upon the initial changed field name. If the affected fields
   * are linked to other visibility conditions then they will be processed as well.
   * @param {string} fieldName - The field name to check the reverse field map for.
   * @param {FieldSetting} parentFieldSetting - Used for recursive calls to determine if a child setting should be visible.
   * @private
   */
  private runConditionTree(fieldName: string, parentFieldSetting?: FieldSetting) {
    // fieldName is the `sourceFieldId_condition type` key
    if (this.reverseFieldMap.has(fieldName)) {
      const subFieldGroups = this.reverseFieldMap.get(fieldName);
      // Checks if there are visibility condition groups defined for this particular `sourceFieldId_condition type` key and iterate over them
      if (subFieldGroups) {
        const previousFieldSetting = parentFieldSetting || this.settingConditionMap.get(fieldName);
        for (let groupIndex = 0, groupLength = subFieldGroups.length; groupIndex < groupLength; groupIndex++) {
          // `subFields` is the array of 'condition id_condition type' strings
          const subFields = subFieldGroups[groupIndex];
          for (let i = 0, length = subFields.length; i < length; i++) {
            // `subFields` is a `condition id_condition type` key which just identifies a set of conditions to be checked
            const subField = subFields[i];
            // based on that key, get the field setting for which the visibility conditions are configured
            const fieldSetting = this.settingConditionMap.get(subField);

            if (fieldSetting) {
              fieldSetting.setFieldVisibility(
                this.data,
                this.userGroups,
                fieldName !== subField ? previousFieldSetting : undefined
              );

              if (fieldSetting.hasSettingChanged) {
                if (!fieldSetting.isButtonSetting) {
                  this.haveFieldSettingsChanged = true;
                } else {
                  this.haveButtonSettingsChanged = true;
                }
              }
            }

            if (fieldSetting && fieldSetting.hasSettingChanged) {
              // When the field setting changes, we need to also run all other visibility conditions tied to the field that just changed
              // This means parsing out the 'fieldName' out the 'subField', which is really the ID of a field that needs to be checked next,
              // as it may also have visibility conditions
              const _fieldName = subField.split('_')[0];
              this.checkAndRunLinkedConditions(_fieldName, fieldSetting);

              // Additionally, if the field setting is a group field, we need to also check all child fields for visibility conditions
              if (!fieldSetting.isButtonSetting) {
                if ('fields' in fieldSetting.field && Array.isArray(fieldSetting.field.fields)) {
                  for (let j = 0, jLength = fieldSetting.field.fields.length; j < jLength; j++) {
                    const childField = fieldSetting.field.fields[j];

                    const _fieldName = childField.fieldName;
                    this.checkAndRunLinkedConditions(_fieldName, fieldSetting);
                  }
                }
              }
            } else {
              if (fieldName !== subField) {
                this.runConditionTree(subField, fieldSetting);
              }
            }
          }
        }
      }
    }
  }

  /**
   * Called when the processor is first instantiated; this method takes the passed field visibility conditions and
   * reverses it so that it works by using the field that was changed to determine which visibility conditions should be
   * run. This will allow the visibility condition code to not have to run all conditions on each field change.
   * @param {Record<string, Array<VisibilityCondition>>} fieldConditions - The visibility conditions to create a reverse map from.
   * @private
   */
  private buildFieldTypeMapAndReverseFieldMap(fieldConditions: Array<VisibilityConditionConfig>) {
    for (let i = 0, length = fieldConditions.length; i < length; i++) {
      const fieldCondition = fieldConditions[i];
      for (let groupIndex = 0, groupLength = fieldCondition.groups.length; groupIndex < groupLength; groupIndex++) {
        const group = fieldCondition.groups[groupIndex];
        for (let j = 0, jLength = group.conditions.length; j < jLength; j++) {
          const condition = group.conditions[j];
          if (condition.sourceFieldId) {
            const sourceFieldId =
              condition.fieldVisibilityType === 'HIERARCHY' ? 'hierarchyValues' : condition.sourceFieldId;
            const sourceFieldKey = `${sourceFieldId}_${fieldCondition.type || 'BUTTON'}`;

            // fieldTypeMap is populated
            if (this.fieldTypeMap.has(sourceFieldId.toString())) {
              const fieldTypes = this.fieldTypeMap.get(sourceFieldId.toString()) || [];
              if (!fieldTypes.includes(fieldCondition.type || 'BUTTON')) {
                fieldTypes.push(fieldCondition.type || 'BUTTON');
                this.fieldTypeMap.set(sourceFieldId.toString(), fieldTypes);
              }
            } else {
              this.fieldTypeMap.set(sourceFieldId.toString(), [fieldCondition.type || 'BUTTON']);
            }

            // reverseFieldMap is populated
            if (!this.reverseFieldMap.has(sourceFieldKey)) {
              this.reverseFieldMap.set(sourceFieldKey, [[`${fieldCondition.id}_${fieldCondition.type || 'BUTTON'}`]]);
            } else {
              const currentFields = this.reverseFieldMap.get(sourceFieldKey) || [[]];
              if (currentFields.length === groupIndex) {
                // if groupIndex is the same as the length then we are in a condition with multiple groups, so we need
                // to add the next group array
                currentFields.push([]);
              }

              if (!currentFields[groupIndex].includes(`${fieldCondition.id}_${fieldCondition.type || 'BUTTON'}`)) {
                currentFields[groupIndex].push(`${fieldCondition.id}_${fieldCondition.type || 'BUTTON'}`);
                this.reverseFieldMap.set(sourceFieldKey, currentFields);
              }
            }
          }
        }
      }
    }
  }

  private buildFieldSettingsMap(
    formFieldSettings: Array<DynamicFormField>,
    fieldConditions: Array<VisibilityConditionConfig>
  ) {
    for (let i = 0, length = fieldConditions.length; i < length; i++) {
      const fieldCondition = fieldConditions[i];
      const fieldSetting = this.findFieldSetting(formFieldSettings, fieldCondition);
      const conditionGroups = this.createConditionGroups(fieldCondition.groups);

      if (fieldSetting && conditionGroups && conditionGroups.length > 0) {
        if (!this.isOnlyGroupConditions(conditionGroups)) {
          this.settingConditionMap.set(
            `${fieldCondition.id}_${fieldCondition.type}`,
            new FieldSetting(fieldSetting, conditionGroups, this.fieldSettingsFlatMap)
          );
        } else {
          this.groupOnlyConditionMap.set(
            `${fieldCondition.id}_${fieldCondition.type}`,
            new FieldSetting(fieldSetting, conditionGroups, this.fieldSettingsFlatMap)
          );
        }
      }
    }
  }

  private findFieldSetting(
    fieldSettings: Array<DynamicFormField>,
    condition: VisibilityConditionConfig
  ): DynamicFormField | undefined {
    for (let i = 0, length = fieldSettings.length; i < length; i++) {
      if (
        (fieldSettings[i].fieldName === String(condition.id) || String(fieldSettings[i].id) === String(condition.id)) &&
        (fieldSettings[i].fieldType === condition.type || fieldSettings[i].customFieldType === condition.type)
      ) {
        return fieldSettings[i];
      }

      const childFields = fieldSettings[i].fields;
      if (childFields) {
        const foundSetting = this.findFieldSetting(childFields, condition);
        if (foundSetting) {
          return foundSetting;
        }
      }
    }

    return undefined;
  }

  private buildButtonSettingsMap(
    formButtonSettings: Array<DynamicFormButtonSetting>,
    workflowStepConditions: Array<VisibilityConditionConfig>
  ) {
    for (let i = 0, length = workflowStepConditions.length; i < length; i++) {
      const workflowStepCondition = workflowStepConditions[i];
      const formButtonSetting = formButtonSettings.find(
        (button) => String(button.id) === String(workflowStepCondition.id)
      );
      const conditionGroups = this.createConditionGroups(workflowStepCondition.groups);

      if (formButtonSetting && conditionGroups && conditionGroups.length > 0) {
        if (!this.isOnlyGroupConditions(conditionGroups)) {
          this.settingConditionMap.set(
            `${workflowStepCondition.id}_${workflowStepCondition.type || 'BUTTON'}`,
            new FieldSetting(formButtonSetting, conditionGroups)
          );
        } else {
          this.groupOnlyConditionMap.set(
            `${workflowStepCondition.id}_${workflowStepCondition.type || 'BUTTON'}`,
            new FieldSetting(formButtonSetting, conditionGroups)
          );
        }
      }
    }
  }

  private createConditionGroups(groups: Array<VisibilityConditionGroup>) {
    const conditionGroups: Array<Array<VisibilityConditionBase>> = [];
    for (let groupIndex = 0, groupLength = groups.length; groupIndex < groupLength; groupIndex++) {
      const group = groups[groupIndex];
      const groupConditions: Array<VisibilityConditionBase> = [];
      for (
        let conditionIndex = 0, conditionLength = group.conditions.length;
        conditionIndex < conditionLength;
        conditionIndex++
      ) {
        const condition = group.conditions[conditionIndex];
        if (condition) {
          const conditionBase = VisibilityConditionFactory.getVisibilityCondition(
            condition.fieldVisibilityType,
            condition
          );
          if (conditionBase) {
            groupConditions.push(conditionBase);
          }
        }
      }

      conditionGroups.push(groupConditions);
    }

    return conditionGroups;
  }

  private hasData() {
    return Object.keys(this.data ?? {}).length > 0;
  }

  private buildFieldSettingsFlatMap(fieldSettings: Array<DynamicFormField>) {
    const fields = fieldSettings.flatMap((fieldSetting) => {
      if (fieldSetting.fields) {
        return [fieldSetting, ...fieldSetting.fields];
      } else {
        return [fieldSetting];
      }
    });

    for (let i = 0, length = fields.length; i < length; i++) {
      this.fieldSettingsFlatMap.set(fields[i].id, fields[i]);
    }
  }

  private updateFieldSettingsFlatMap(fieldSettings: Array<DynamicFormField>) {
    this.fieldSettingsFlatMap.clear();
    this.buildFieldSettingsFlatMap(fieldSettings);
  }

  private checkAndRunLinkedConditions = (fieldNameToCheck: string, fieldSetting: FieldSetting) => {
    if (this.fieldTypeMap.has(fieldNameToCheck)) {
      const fieldTypes = this.fieldTypeMap.get(fieldNameToCheck) || [];
      for (let i = 0, length = fieldTypes.length; i < length; i++) {
        this.runConditionTree(`${fieldNameToCheck}_${fieldTypes[i]}`, fieldSetting);
      }
    }
  };
}
